import React, { useMemo, useState } from "react";
import {
  ExternalLink,
  FileText,
  Save,
  Folder,
  ChevronRight,
  ChevronDown,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { folderService, FolderTreeNode } from "@/services/folderService";
import { aiAssistantService } from "@/services/aiAssistantService";

interface SaveToolOutputButtonProps {
  toolName: string;
  assistantInstanceId?: string;
  outputId?: string | null;
  className?: string;
}

export function SaveToolOutputButton({
  toolName,
  assistantInstanceId,
  outputId,
  className = "",
}: SaveToolOutputButtonProps) {
  const { organization } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [showAllFolders, setShowAllFolders] = useState(false);
  const [title, setTitle] = useState<string>(
    toolName ? `${toolName} Output` : "Generated Output",
  );

  // Load folder tree when dialog is open
  const { data: folderTree } = useQuery({
    queryKey: ["folderTree", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [] as FolderTreeNode[];
      const result = await folderService.getFolderTree(organization.id);
      if (result.error) throw result.error;
      return result.data || [];
    },
    enabled: !!organization?.id && (isDialogOpen || !!outputId),
  });

  // Fetch current output info to know if already saved and in which folder
  const { data: outputInfo } = useQuery({
    queryKey: ["toolOutput", outputId],
    queryFn: async () => {
      if (!outputId) return null;
      const { data, error } = await aiAssistantService.getOutputById(outputId);
      if (error) throw error;
      return data;
    },
    enabled: !!outputId,
  });

  const isSaved = !!outputInfo?.is_saved;
  const folderId = outputInfo?.folder_id as string | undefined;
  const outputTitle = (outputInfo?.output_title as string | undefined) || title;
  const currentFolder = useMemo(() => {
    if (!outputInfo?.folder_id || !folderTree) return null;
    return folderTree.find((f) => f.id === outputInfo.folder_id) || null;
  }, [outputInfo?.folder_id, folderTree]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!outputId) throw new Error("No output to save");
      if (!selectedFolderId) throw new Error("Select a folder first");
      // Update title if changed
      if (title && title !== outputTitle) {
        const { error: titleErr } =
          await aiAssistantService.updateAIOutputTitle(outputId, title);
        if (titleErr) throw titleErr;
      }

      const { error } = await aiAssistantService.saveAIOutput(
        outputId,
        selectedFolderId,
      );
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["toolOutput", outputId] });
      queryClient.invalidateQueries({
        queryKey: ["folderTree", organization?.id],
      });
      setIsDialogOpen(false);
      toast({
        title: "Output saved",
        description: "Added to selected folder.",
      });
    },
    onError: (e: any) => {
      toast({
        title: "Save failed",
        description: e?.message || "Couldn't save output",
        variant: "destructive",
      });
    },
  });

  const handleOpenOutput = () => {
    if (outputId && assistantInstanceId) {
      window.open(
        `/ai-assistants/${assistantInstanceId}?output=${outputId}&tab=results`,
        "_blank",
      );
    } else {
      window.open("/library", "_blank");
    }
  };

  const handleToggleExpanded = (folderId: string) => {
    const next = new Set(expandedFolders);
    if (next.has(folderId)) next.delete(folderId);
    else next.add(folderId);
    setExpandedFolders(next);
  };

  const FolderRow = ({ node }: { node: FolderTreeNode }) => {
    const isExpanded = expandedFolders.has(node.id);
    const isSelected = selectedFolderId === node.id;
    const hasChildren = false; // tree shape simplified here
    return (
      <div
        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? "bg-blue-50 border border-blue-200" : ""}`}
        style={{ marginLeft: `${node.depth * 20}px` }}
        onClick={() => setSelectedFolderId(node.id)}
      >
        {hasChildren ? (
          <button
            className="p-1 hover:bg-gray-200 rounded"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleExpanded(node.id);
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        ) : (
          <div className="w-5" />
        )}
        <Folder className="h-4 w-4 text-gray-600" />
        <span className="text-sm truncate flex-1">{node.name}</span>
      </div>
    );
  };

  // If already saved and we know the folder, show saved state
  if (outputId && isSaved) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Check className="h-4 w-4 text-green-600" />
        {currentFolder ? (
          <>
            <span className="text-xs text-gray-600">Saved in</span>
            <button
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              onClick={() =>
                window.open(`/folders?folder=${currentFolder.id}`, "_blank")
              }
            >
              {currentFolder.name}
            </button>
          </>
        ) : (
          <span className="text-xs text-gray-600">Saved</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            window.open(
              folderId ? `/folders?folder=${folderId}` : "/folders",
              "_blank",
            )
          }
          className="text-blue-600 hover:text-blue-800 p-0 h-auto font-normal"
        >
          View in folder
          <ExternalLink className="h-3 w-3 ml-1" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className={`flex items-center gap-3 ${className}`}>
        <FileText className="h-4 w-4 text-blue-500" />
        <Button
          variant="outline"
          size="sm"
          disabled={!outputId}
          onClick={() => setIsDialogOpen(true)}
        >
          <Save className="h-3 w-3 mr-1" /> Save output to folder
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Output to Folder</DialogTitle>
            <DialogDescription>
              Choose a destination folder for this tool output.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="output-title">Title</Label>
              <Input
                id="output-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="max-h-60 overflow-y-auto border rounded-md">
              {folderTree && folderTree.length > 0 ? (
                <div className="p-2">
                  {(showAllFolders ? folderTree : folderTree.slice(0, 6)).map(
                    (node) => (
                      <FolderRow key={node.id} node={node} />
                    ),
                  )}
                  {folderTree.length > 6 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllFolders(!showAllFolders)}
                        className="w-full justify-center text-xs text-gray-600 hover:text-gray-800"
                      >
                        {showAllFolders ? (
                          <>
                            <ChevronDown className="h-3 w-3 mr-1" /> Show Less
                          </>
                        ) : (
                          <>
                            <ChevronRight className="h-3 w-3 mr-1" /> More (
                            {folderTree.length - 6} more)
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 text-center text-sm text-gray-500">
                  No folders found. Create folders in the Library.
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={
                saveMutation.isPending || !selectedFolderId || !outputId
              }
            >
              {saveMutation.isPending ? "Saving..." : "Save Output"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
